import component from '@123/druid/dist/Framework/Decorators/Component';
import {inject, injectable} from '@123/druid/dist/Framework/Decorators/Inject';
import type {AnalyticsScript} from '@Module/Tracking/Schema/AnalyticsScript';
import CookieTrackingService from '@Module/Tracking/Service/CookieTrackingService';
import type {ParsedHTML} from '@Module/Tracking/Type/ParsedHTML';
import Measure from '@Service/LegacyBridge/Measure/Measure';
import DomAppender from '@Utility/Dom/DomAppender';

@component
@injectable()
export default class CookieTracking {
    constructor(@inject(CookieTrackingService) private service: CookieTrackingService) {}

    public async updateCookiePreference(cookiePreference: boolean): Promise<void> {
        const response = await this.service.updateCookiePreferences({
            pageType: window.pageType,
            cookiePreference,
        });

        if (response.isConsentRequired && cookiePreference === false) {
            return;
        }

        this.insertTrackingScripts(response.tracking);

        if (response.html !== '') {
            const parsedElements = this.parseHTMLByTag(response.html);
            await this.insertTemplateScripts(parsedElements, cookiePreference);
        }
    }

    private insertTrackingScripts(tracking: AnalyticsScript['tracking']): void {
        for (const meta of Object.values(tracking)) {
            const parsedContent = this.parseHTMLByTag(meta);
            this.appendScripts(parsedContent);
        }
    }

    private async insertTemplateScripts(elements: ParsedHTML, cookiePreference: boolean): Promise<void> {
        if (cookiePreference && Measure.isRegistered() === false) {
            this.appendScripts(elements);

            Measure.register();
            await Measure.cookiePermission(cookiePreference, false);
            return;
        }

        await Measure.cookiePermission(cookiePreference, true);
    }

    private parseHTMLByTag(htmlString: string): ParsedHTML {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;

        const result: ParsedHTML = {script: [], other: []};

        tempDiv.childNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;

            const tagName = node.tagName.toLowerCase();
            if (tagName === 'script') {
                result.script.push(<HTMLScriptElement>node);
            } else {
                result.other.push(node);
            }
        });

        return result;
    }

    private appendScripts(tags: ParsedHTML): void {
        for (const element of tags.script) {
            DomAppender.appendScript(document.head, element);
        }

        for (const element of tags.other) {
            document.head.append(element);
        }
    }
}
